import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { storage } from '../utils/storage';
import { db } from '../config/firebase';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { walletService } from '../services/walletService';
import { fetchWithAuth } from '../services/authToken';

interface Setor {
  nome: string;
  valor: number;
}

interface Entrega {
  nomeCliente: string;
  telefone: string;
  endereco: {
    rua: string;
    numero: string;
  };
  setor: string;
  adicional: string;
  tempoProgramado?: number;
}

const ChamadoDetalhado: React.FC = () => {
  const navigate = useNavigate();
  const [entregas, setEntregas] = useState<Entrega[]>([{
    nomeCliente: '',
    telefone: '',
    endereco: { rua: '', numero: '' },
    setor: '',
    adicional: '',
    tempoProgramado: 0
  }]);
  const [setores, setSetores] = useState<Setor[]>([]);
  const [setoresFiltrados, setSetoresFiltrados] = useState<Setor[]>([]);
  const [pesquisaSetor, setPesquisaSetor] = useState('');
  const [showClientesDropdown, setShowClientesDropdown] = useState(false);
  const [clientes, setClientes] = useState<any[]>([]);
  const [clientesFiltrados, setClientesFiltrados] = useState<any[]>([]);
  const [pesquisaCliente, setPesquisaCliente] = useState('');
  const [empresaId, setEmpresaId] = useState('');
  const [empresaNome, setEmpresaNome] = useState('');
  const [userId, setUserId] = useState<string | null>(null);
  const [usaCarteira, setUsaCarteira] = useState(true);
  const [taxaMotoboyAtiva, setTaxaMotoboyAtiva] = useState(false);
  const [taxaMotoboyValor, setTaxaMotoboyValor] = useState(0);
  const [saldoCarteira, setSaldoCarteira] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formaPagamento, setFormaPagamento] = useState('');
  const [detalhesProduto, setDetalhesProduto] = useState('');
  const [valorReceber, setValorReceber] = useState('');
  const [apenasEntrega, setApenasEntrega] = useState(false);
  const [contemBebidas, setContemBebidas] = useState(false);
  const [paradaBuscarCondicional, setParadaBuscarCondicional] = useState(false);
  const [showDetalhesAdicionais, setShowDetalhesAdicionais] = useState(false);
  const [mensalidadesAtraso, setMensalidadesAtraso] = useState<{
    temAtraso: boolean;
    bloqueado: boolean;
  }>({ temAtraso: false, bloqueado: false });

  const opcoesTempoProgramado = [
    { label: 'Imediato', valor: 0 },
    { label: '10 minutos', valor: 10 },
    { label: '15 minutos', valor: 15 },
    { label: '20 minutos', valor: 20 },
    { label: '30 minutos', valor: 30 },
    { label: '1 hora', valor: 60 },
    { label: '2 horas', valor: 120 },
    { label: '3 horas', valor: 180 },
  ];

  // Carregar dados iniciais
  useEffect(() => {
    const carregarDados = async () => {
      try {
        const id = await storage.getItem('userId');
        if (!id) {
          navigate('/login');
          return;
        }
        setUserId(id);

        const userDoc = await getDoc(doc(db, 'usuarios', id));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.empresaId) {
            setEmpresaId(userData.empresaId);
            const empresaDoc = await getDoc(doc(db, 'Empresa', userData.empresaId));
            if (empresaDoc.exists()) {
              const empresaData = empresaDoc.data();
              setEmpresaNome(empresaData.nomeEmpresa || 'Empresa');
              setUsaCarteira(empresaData.usaCarteira === true);
              
              if (empresaData.usaCarteira) {
                const saldoDisponivel = await walletService.getAvailableBalance(userData.empresaId);
                setSaldoCarteira(saldoDisponivel);
              }

              if (empresaData.tabelaTaxaId) {
                await carregarSetores(empresaData.tabelaTaxaId);
              }

              // Carregar configurações
              try {
                const empresaConfigRef = doc(db, 'Empresa', userData.empresaId, 'config', 'central');
                const empresaConfigSnap = await getDoc(empresaConfigRef);
                if (empresaConfigSnap.exists()) {
                  const cfg = empresaConfigSnap.data();
                  setTaxaMotoboyAtiva(!!cfg.habilitarTaxaMotoboy);
                  setTaxaMotoboyValor(Number(cfg.taxaMotoboyValor || 0));
                }
              } catch (e) {
                console.warn('Erro ao carregar configurações:', e);
              }
            }
          }
        }
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      }
    };
    carregarDados();
  }, [navigate]);

  // Carregar setores
  const carregarSetores = async (tabelaTaxaId: string) => {
    try {
      const tabelaDoc = await getDoc(doc(db, 'tabelasTaxas', tabelaTaxaId));
      if (tabelaDoc.exists()) {
        const tabelaData = tabelaDoc.data();
        const setoresData = tabelaData.setores || [];
        setSetores(setoresData);
        setSetoresFiltrados(setoresData);
      }
    } catch (error) {
      console.error('Erro ao carregar setores:', error);
    }
  };

  // Verificar mensalidades em atraso
  const verificarMensalidadesAtraso = async (empresaId: string) => {
    try {
      const empresaDoc = await getDoc(doc(db, 'Empresa', empresaId));
      if (!empresaDoc.exists()) {
        return { temAtraso: false, bloqueado: false };
      }

      const empresaData = empresaDoc.data();
      
      if (empresaData.cobrancaRecorrenteAtiva && empresaData.cobrancaRecorrenteId) {
        const cobrancaDoc = await getDoc(doc(db, 'Empresa', empresaId, 'cobrancasRecorrentes', empresaData.cobrancaRecorrenteId));
        if (cobrancaDoc.exists()) {
          const cobrancaData = cobrancaDoc.data();
          const parcelas = cobrancaData.parcelas || [];
          const carenciaEmpresa = cobrancaData.carenciaEmpresa || 7;
          
          const hoje = new Date();
          const parcelasAtrasadas = parcelas.filter((parcela: any) => {
            if (parcela.status === 'paga') return false;
            const dataVencimento = parcela.dataVencimento?.toDate ? parcela.dataVencimento.toDate() : new Date(parcela.dataVencimento);
            return dataVencimento < hoje;
          });

          let bloqueado = false;
          if (parcelasAtrasadas.length > 0) {
            const parcelaMaisAntiga = parcelasAtrasadas.reduce((maisAntiga: any, parcela: any) => {
              const dataVencimento = parcela.dataVencimento?.toDate ? parcela.dataVencimento.toDate() : new Date(parcela.dataVencimento);
              const dataMaisAntiga = maisAntiga.dataVencimento?.toDate ? maisAntiga.dataVencimento.toDate() : new Date(maisAntiga.dataVencimento);
              return dataVencimento < dataMaisAntiga ? parcela : maisAntiga;
            });

            const dataVencimentoMaisAntiga = parcelaMaisAntiga.dataVencimento?.toDate ? parcelaMaisAntiga.dataVencimento.toDate() : new Date(parcelaMaisAntiga.dataVencimento);
            const diasEmAtraso = Math.floor((hoje.getTime() - dataVencimentoMaisAntiga.getTime()) / (1000 * 60 * 60 * 24));
            bloqueado = diasEmAtraso > carenciaEmpresa;
          }

          return {
            temAtraso: parcelasAtrasadas.length > 0,
            bloqueado: bloqueado
          };
        }
      }

      return { temAtraso: false, bloqueado: false };
    } catch (error) {
      console.error('Erro ao verificar mensalidades:', error);
      return { temAtraso: false, bloqueado: false };
    }
  };

  useEffect(() => {
    if (empresaId) {
      const verificar = async () => {
        const resultado = await verificarMensalidadesAtraso(empresaId);
        setMensalidadesAtraso(resultado);
      };
      verificar();
    }
  }, [empresaId]);

  // Carregar clientes
  useEffect(() => {
    const carregarClientes = async () => {
      if (!userId) return;
      try {
        const clientesRef = collection(db, 'usuarios', userId, 'clientes');
        const snapshot = await getDocs(clientesRef);
        const clientesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setClientes(clientesData);
      } catch (error) {
        console.error('Erro ao carregar clientes:', error);
      }
    };
    carregarClientes();
  }, [userId]);

  // Filtrar setores
  useEffect(() => {
    if (pesquisaSetor.trim() === '') {
      setSetoresFiltrados(setores);
    } else {
      const filtrados = setores.filter(setor =>
        setor.nome.toLowerCase().includes(pesquisaSetor.toLowerCase())
      );
      setSetoresFiltrados(filtrados);
    }
  }, [pesquisaSetor, setores]);

  // Filtrar clientes
  useEffect(() => {
    if (pesquisaCliente.trim() === '') {
      setShowClientesDropdown(false);
      setClientesFiltrados([]);
    } else {
      const filtrados = clientes.filter(cliente =>
        cliente.nome?.toLowerCase().includes(pesquisaCliente.toLowerCase()) ||
        cliente.telefone?.includes(pesquisaCliente)
      );
      setClientesFiltrados(filtrados);
      setShowClientesDropdown(filtrados.length > 0);
    }
  }, [pesquisaCliente, clientes]);

  const adicionarEntrega = () => {
    setEntregas([...entregas, {
      nomeCliente: '',
      telefone: '',
      endereco: { rua: '', numero: '' },
      setor: '',
      adicional: '',
      tempoProgramado: 0
    }]);
  };

  const removerEntrega = (index: number) => {
    if (entregas.length > 1) {
      setEntregas(entregas.filter((_, idx) => idx !== index));
    } else {
      alert('É necessário ter pelo menos uma entrega no chamado.');
    }
  };

  const atualizarEntrega = (index: number, campo: keyof Entrega | 'endereco.rua' | 'endereco.numero', valor: string | number) => {
    const novasEntregas = [...entregas];
    if (campo === 'endereco.rua') {
      novasEntregas[index].endereco.rua = valor as string;
    } else if (campo === 'endereco.numero') {
      novasEntregas[index].endereco.numero = valor as string;
    } else if (campo === 'tempoProgramado') {
      novasEntregas[index].tempoProgramado = valor as number;
      (novasEntregas[index] as any)[campo] = valor;
    }
    setEntregas(novasEntregas);
  };

  const selecionarCliente = (cliente: any, index: number) => {
    const enderecos = cliente.enderecos && Array.isArray(cliente.enderecos)
      ? cliente.enderecos
      : (cliente.endereco ? [cliente.endereco] : []);

    if (enderecos.length > 0) {
      const endereco = enderecos[0];
      atualizarEntrega(index, 'nomeCliente', cliente.nome || '');
      atualizarEntrega(index, 'telefone', cliente.telefone || '');
      atualizarEntrega(index, 'endereco.rua', endereco.rua || '');
      atualizarEntrega(index, 'endereco.numero', endereco.numero || '');
      atualizarEntrega(index, 'adicional', endereco.adicional || '');
      atualizarEntrega(index, 'setor', endereco.setor || '');
    } else {
      atualizarEntrega(index, 'nomeCliente', cliente.nome || '');
      atualizarEntrega(index, 'telefone', cliente.telefone || '');
    }
    setPesquisaCliente('');
    setShowClientesDropdown(false);
  };

  const calcularTotal = () => {
    let total = 0;
    entregas.forEach(entrega => {
      const setor = setores.find(s => s.nome === entrega.setor);
      if (setor) {
        const valorSetor = Number(setor.valor || 0);
        const taxa = usaCarteira && taxaMotoboyAtiva ? Number(taxaMotoboyValor || 0) : 0;
        total += valorSetor + taxa;
      }
    });
    return total;
  };

  const handleCriarChamado = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // Verificar mensalidades em atraso
      if (mensalidadesAtraso.temAtraso && mensalidadesAtraso.bloqueado) {
        alert('Seu acesso foi bloqueado devido a mensalidades em atraso. Regularize suas cobranças recorrentes para voltar a criar chamados.');
        setIsSubmitting(false);
        return;
      }

      // Validações
      const entregasSemSetor = entregas.some(e => !e.setor);
      if (entregasSemSetor) {
        alert('Por favor, selecione o setor para todas as entregas.');
        setIsSubmitting(false);
        return;
      }

      const entregasInvalidas = entregas.some(e => !e.nomeCliente || !e.telefone || !e.endereco.rua || !e.endereco.numero);
      if (entregasInvalidas) {
        alert('Preencha todos os campos obrigatórios para todas as entregas.');
        setIsSubmitting(false);
        return;
      }

      // Verificar frete zero
      const entregasComFreteZero = entregas.filter(entrega => {
        const setorObj = setores.find(s => s.nome === entrega.setor);
        const frete = setorObj ? Number(setorObj.valor || 0) : 0;
        return isNaN(frete) || frete <= 0;
      });
      if (entregasComFreteZero.length > 0) {
        alert('Há entregas com frete zerado. Configure os valores da tabela de taxas antes de criar o chamado.');
        setIsSubmitting(false);
        return;
      }

      // Verificar saldo
      const total = calcularTotal();
      if (usaCarteira) {
        const saldoDisponivel = await walletService.getAvailableBalance(empresaId);
        if (saldoDisponivel < total) {
          alert(`Saldo insuficiente. Necessário: R$ ${total.toFixed(2).replace('.', ',')}, Disponível: R$ ${saldoDisponivel.toFixed(2).replace('.', ',')}`);
          setIsSubmitting(false);
          return;
        }
      }

      // Separar entregas imediatas e agendadas
      const entregasImediatas = entregas.filter(e => !e.tempoProgramado || e.tempoProgramado === 0);
      const entregasAgendadas = entregas.filter(e => e.tempoProgramado && e.tempoProgramado > 0);

      // Criar chamados imediatos
      if (entregasImediatas.length > 0) {
        const entregasFormatadas = entregasImediatas.map(e => ({
          nomeCliente: e.nomeCliente,
          telefone: e.telefone,
          endereco: e.endereco,
          setor: e.setor,
          adicional: e.adicional,
          tempoProgramado: e.tempoProgramado || 0
        }));

        const response = await fetchWithAuth(
          'https://us-central1-vs-sistemadeentregas.cloudfunctions.net/criarChamadosEmGrupo',
          {
            method: 'POST',
            wrapAsCallableData: true,
            body: {
              entregas: entregasFormatadas,
              empresaId,
              empresaNome,
              usaCarteira,
              taxaMotoboyAtiva,
              taxaMotoboyValor,
              setores,
              tipo: 'detalhada',
              contemBebidas,
              apenasEntrega,
              detalhesProduto,
              valorReceber,
              formaPagamento,
              paradaBuscarCondicional,
            }
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText || 'Erro ao criar chamados');
        }
      }

      // Criar chamados agendados
      if (entregasAgendadas.length > 0) {
        const now = Date.now();
        const payloadAgendadas = entregasAgendadas.map(e => ({
          nomeCliente: e.nomeCliente,
          telefone: e.telefone,
          endereco: e.endereco,
          setor: e.setor,
          adicional: e.adicional,
          tempoProgramado: e.tempoProgramado || 0,
          agendada: true,
          tempoProgramadoMin: Number(e.tempoProgramado || 0),
          liberadoEmMs: now + Math.max(0, Number(e.tempoProgramado || 0)) * 60 * 1000,
        }));

        const responseAgendadas = await fetchWithAuth(
          'https://us-central1-vs-sistemadeentregas.cloudfunctions.net/criarChamadosEmGrupo',
          {
            method: 'POST',
            wrapAsCallableData: true,
            body: {
              entregas: payloadAgendadas,
              empresaId,
              empresaNome,
              usaCarteira,
              taxaMotoboyAtiva,
              taxaMotoboyValor,
              setores,
              tipo: 'detalhada',
              contemBebidas,
              apenasEntrega,
              detalhesProduto,
              valorReceber,
              formaPagamento,
              paradaBuscarCondicional,
              propagarPorFila: true
            }
          }
        );

        if (!responseAgendadas.ok) {
          const errorText = await responseAgendadas.text();
          throw new Error(errorText || 'Erro ao criar chamados agendados');
        }
      }

      alert('Chamado(s) criado(s) com sucesso!');
      navigate('/status-pedido');
    } catch (error: any) {
      console.error('Erro ao criar chamado:', error);
      alert(error.message || 'Erro ao criar chamado. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-gray-200/50 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <button
              onClick={() => navigate('/empresa')}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Chamado Detalhado</h1>
              <p className="text-sm text-gray-500">Crie entregas completas</p>
            </div>
            <div className="w-10"></div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-8 py-8">
        {/* Card de Informações */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">Informações da Entrega</h2>
            {usaCarteira && (
              <div className="text-sm">
                <span className="text-gray-600">Saldo: </span>
                <span className="font-bold text-blue-600">R$ {saldoCarteira.toFixed(2).replace('.', ',')}</span>
              </div>
            )}
          </div>
          <div className="text-sm text-gray-600 mb-4">
            Total estimado: <span className="font-bold text-gray-900">R$ {calcularTotal().toFixed(2).replace('.', ',')}</span>
          </div>
        </div>

        {/* Lista de Entregas */}
        <div className="space-y-6 mb-6">
          {entregas.map((entrega, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Entrega {index + 1}</h3>
                {entregas.length > 1 && (
                  <button
                    onClick={() => removerEntrega(index)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>

              <div className="space-y-4">
                {/* Buscar/Inserir Nome do Cliente */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Nome do Cliente *</label>
                  <input
                    type="text"
                    value={entrega.nomeCliente}
                    onChange={(e) => {
                      atualizarEntrega(index, 'nomeCliente', e.target.value);
                      setPesquisaCliente(e.target.value);
                      if (e.target.value) {
                        setShowClientesDropdown(true);
                      } else {
                        setShowClientesDropdown(false);
                      }
                    }}
                    onFocus={() => {
                      if (entrega.nomeCliente) {
                        setPesquisaCliente(entrega.nomeCliente);
                        const filtrados = clientes.filter(cliente =>
                          cliente.nome?.toLowerCase().includes(entrega.nomeCliente.toLowerCase()) ||
                          cliente.telefone?.includes(entrega.nomeCliente)
                        );
                        setClientesFiltrados(filtrados);
                        if (filtrados.length > 0) {
                          setShowClientesDropdown(true);
                        }
                      }
                    }}
                    onBlur={() => {
                      // Delay para permitir clique no dropdown
                      setTimeout(() => setShowClientesDropdown(false), 200);
                    }}
                    placeholder="Buscar cliente ou digitar nome"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900"
                  />
                  {showClientesDropdown && clientesFiltrados.length > 0 && (
                    <div 
                      className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto"
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {clientesFiltrados.map((cliente) => (
                        <button
                          key={cliente.id}
                          onClick={() => selecionarCliente(cliente, index)}
                          className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0"
                        >
                          <div className="font-semibold text-gray-900">{cliente.nome}</div>
                          <div className="text-sm text-gray-600">{cliente.telefone}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Telefone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Telefone *</label>
                  <input
                    type="tel"
                    value={entrega.telefone}
                    onChange={(e) => atualizarEntrega(index, 'telefone', e.target.value)}
                    placeholder="(00) 00000-0000"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900"
                  />
                </div>

                {/* Endereço */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Rua *</label>
                    <input
                      type="text"
                      value={entrega.endereco.rua}
                      onChange={(e) => atualizarEntrega(index, 'endereco.rua', e.target.value)}
                      placeholder="Nome da rua"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Número *</label>
                    <input
                      type="text"
                      value={entrega.endereco.numero}
                      onChange={(e) => atualizarEntrega(index, 'endereco.numero', e.target.value)}
                      placeholder="Número"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900"
                    />
                  </div>
                </div>

                {/* Setor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Setor *</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={pesquisaSetor}
                      onChange={(e) => setPesquisaSetor(e.target.value)}
                      placeholder="Buscar setor"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900"
                    />
                    {pesquisaSetor && setoresFiltrados.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto">
                        {setoresFiltrados.map((setor, idx) => {
                          const taxa = usaCarteira && taxaMotoboyAtiva ? Number(taxaMotoboyValor || 0) : 0;
                          const valorFinal = Number(setor.valor || 0) + taxa;
                          return (
                            <button
                              key={idx}
                              onClick={() => {
                                atualizarEntrega(index, 'setor', setor.nome);
                                setPesquisaSetor('');
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-0"
                            >
                              <div className="font-semibold text-gray-900">{setor.nome}</div>
                              <div className="text-sm text-blue-600 font-medium">R$ {valorFinal.toFixed(2).replace('.', ',')}</div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  {entrega.setor && (
                    <div className="mt-2 px-3 py-2 bg-blue-50 rounded-lg inline-block">
                      <span className="text-sm font-medium text-blue-700">{entrega.setor}</span>
                    </div>
                  )}
                </div>

                {/* Adicional */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Observações Adicionais</label>
                  <textarea
                    value={entrega.adicional}
                    onChange={(e) => atualizarEntrega(index, 'adicional', e.target.value)}
                    placeholder="Complemento, referência, etc."
                    rows={3}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all resize-none text-gray-900"
                  />
                </div>

                {/* Tempo Programado */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tempo Programado</label>
                  <select
                    value={entrega.tempoProgramado || 0}
                    onChange={(e) => atualizarEntrega(index, 'tempoProgramado', Number(e.target.value))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900"
                  >
                    {opcoesTempoProgramado.map(opcao => (
                      <option key={opcao.valor} value={opcao.valor}>{opcao.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Botão Adicionar Entrega */}
        <button
          onClick={adicionarEntrega}
          className="w-full py-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-all mb-6 font-semibold"
        >
          + Adicionar Outra Entrega
        </button>

        {/* Detalhes Adicionais */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <button
            onClick={() => setShowDetalhesAdicionais(!showDetalhesAdicionais)}
            className="w-full flex items-center justify-between text-left"
          >
            <h3 className="text-lg font-semibold text-gray-900">Detalhes Adicionais</h3>
            <svg
              className={`w-5 h-5 text-gray-600 transform transition-transform ${showDetalhesAdicionais ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showDetalhesAdicionais && (
            <div className="mt-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Forma de Pagamento</label>
                <input
                  type="text"
                  value={formaPagamento}
                  onChange={(e) => setFormaPagamento(e.target.value)}
                  placeholder="Dinheiro, Cartão, PIX, etc."
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Detalhes do Produto</label>
                <textarea
                  value={detalhesProduto}
                  onChange={(e) => setDetalhesProduto(e.target.value)}
                  placeholder="Descrição dos produtos"
                  rows={3}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all resize-none text-gray-900"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Valor a Receber</label>
                <input
                  type="text"
                  value={valorReceber}
                  onChange={(e) => setValorReceber(e.target.value)}
                  placeholder="R$ 0,00"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-gray-900"
                />
              </div>

              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={apenasEntrega}
                    onChange={(e) => setApenasEntrega(e.target.checked)}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Apenas Entrega</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={contemBebidas}
                    onChange={(e) => setContemBebidas(e.target.checked)}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Contém Bebidas</span>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={paradaBuscarCondicional}
                    onChange={(e) => setParadaBuscarCondicional(e.target.checked)}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Parada para Buscar (Condicional)</span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Botões de Ação */}
        <div className="flex gap-4">
          <button
            onClick={() => navigate('/empresa')}
            className="flex-1 px-6 py-4 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
          >
            Cancelar
          </button>
          <button
            onClick={handleCriarChamado}
            disabled={isSubmitting}
            className="flex-1 px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Criando...
              </span>
            ) : (
              'Criar Chamado'
            )}
          </button>
        </div>
      </main>
    </div>
  );
};

export default ChamadoDetalhado;

